/**
 * Settings page
 * Manage profile and billing
 */

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BillingStatus } from "@/features/billing/components/BillingStatus";
import { isFeatureEnabled } from "@/services/feature-flags";
import { updateUserPreferences } from "@/services/api-client";
import { notifyError } from "@/lib/toast";
import {
  type AppLanguage,
  applyAppLanguageChange,
  getStoredDefaultMarket,
  getStoredReportLanguage,
  setDefaultMarket as persistDefaultMarket,
  setReportLanguage as persistReportLanguage,
} from "@/utils/language-preference";
import type { Market } from "@vardic/shared/types.ts";
import { useAuth } from "@/contexts/useAuth";
import SettingsAccountSection from "./settings/SettingsAccountSection";
import SettingsCompanySection from "./settings/SettingsCompanySection";
import SettingsLegalSection from "./settings/SettingsLegalSection";
import SettingsPreferencesSection from "./settings/SettingsPreferencesSection";
import SettingsTeamsSection from "./settings/SettingsTeamsSection";
import SettingsStylesSection from "./settings/SettingsStylesSection";
import SettingsTestingSection from "./settings/SettingsTestingSection";
import { useSettingsProfile } from "./settings/useSettingsProfile";
import { Page } from "@/components/layout/Page";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@/components/ui/tabs";

export default function Settings() {
  const { t, i18n } = useTranslation(["settings", "common"]);
  // Single source of truth for the UI language - i18next's resolved language,
  // shared with the nav LanguageToggle. Avoids two independent local-state
  // snapshots drifting out of sync (vardic review 2026-08-02).
  const appLanguage: AppLanguage = i18n.resolvedLanguage === "en" ? "en" : "pl";
  const [reportLanguage, setReportLanguage] = useState<AppLanguage>(
    getStoredReportLanguage,
  );
  const [defaultMarket, setDefaultMarketState] = useState<Market>(
    getStoredDefaultMarket,
  );
  const [prefStatus, setPrefStatus] = useState<string | null>(null);
  const prefSavedTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  // Tabs stay mounted only once actually visited, so the Teams panel's
  // getTeam() network call (and similar per-panel fetches) doesn't fire on
  // every Settings visit regardless of which tab is active.
  const [activeTab, setActiveTab] = useState("company");
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(
    () => new Set(["company"]),
  );
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setVisitedTabs((prev) => (prev.has(value) ? prev : new Set(prev).add(value)));
  };
  const {
    profileData,
    profileStatus,
    profileError,
    profileLoading,
    profileSaving,
    handleProfileChange,
    handleIndustryToggle,
    handleCertificateToggle,
    handleProfileSave,
  } = useSettingsProfile();

  const showPrefSaved = () => {
    setPrefStatus(t("settings:success.preferencesSaved"));
    clearTimeout(prefSavedTimeoutRef.current);
    prefSavedTimeoutRef.current = setTimeout(() => {
      setPrefStatus(null);
      prefSavedTimeoutRef.current = undefined;
    }, 3000);
  };

  const handleChangeAppLanguage = (lang: string) => {
    const language = lang === "en" ? "en" : "pl";
    // UI language only; the analysis-language control is independent (pirx-m6ks).
    // applyAppLanguageChange persists (localStorage + i18next) and returns the
    // preferences payload for the best-effort cross-browser backend sync.
    const preferences = applyAppLanguageChange(language);
    updateUserPreferences(preferences)
      .then(() => showPrefSaved())
      .catch(() => notifyError(t("settings:errors.preferencesSaveFailed")));
  };

  const handleChangeReportLanguage = (lang: string) => {
    const language = lang === "en" ? "en" : "pl";
    setReportLanguage(language);
    persistReportLanguage(language);
    updateUserPreferences({ report_language: language })
      .then(() => showPrefSaved())
      .catch(() => notifyError(t("settings:errors.preferencesSaveFailed")));
  };

  const handleChangeDefaultMarket = (value: string) => {
    const market = value as Market;
    setDefaultMarketState(market);
    persistDefaultMarket(market);
    updateUserPreferences({ default_market: market })
      .then(() => showPrefSaved())
      .catch(() => notifyError(t("settings:errors.preferencesSaveFailed")));
  };

  const showTeams = isFeatureEnabled("TEAMS");
  const showStyles = isFeatureEnabled("CUSTOM_REWRITE_STYLES");
  const showBilling = isFeatureEnabled("BILLING_SUBSCRIPTIONS") ||
    isFeatureEnabled("BILLING_PREPAID") ||
    isFeatureEnabled("BILLING_ADMIN_TOP_UP_ONLY");
  const { profile } = useAuth();
  const showTesting = profile?.isInternalTester === true;

  return (
    <Page max={960}>
      <PageHeader sub={t("settings:subtitle")} title={t("settings:title")} />

      {/* Left tab subnav (vertical on desktop; scrolls horizontally on mobile).
          Visited panels stay mounted so their section state survives tab
          switches; unvisited panels don't mount at all (see visitedTabs). */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => handleTabChange(value as string)}
        orientation="vertical"
        className="gap-6 lg:items-start"
      >
        <TabsList
          variant="underline"
          className="lg:w-48 lg:shrink-0"
          aria-label={t("settings:title")}
        >
          <TabsTab value="company" data-test="settings-tab-company">
            {t("settings:sections.company")}
          </TabsTab>
          <TabsTab value="preferences" data-test="settings-tab-preferences">
            {t("settings:sections.preferences")}
          </TabsTab>
          <TabsTab value="account" data-test="settings-tab-account">
            {t("settings:sections.account")}
          </TabsTab>
          {showTeams && (
            <TabsTab value="teams" data-test="settings-tab-teams">
              {t("settings:sections.teams")}
            </TabsTab>
          )}
          {showStyles && (
            <TabsTab value="styles" data-test="settings-tab-styles">
              {t("settings:sections.styles")}
            </TabsTab>
          )}
          {showBilling && (
            <TabsTab value="billing" data-test="settings-tab-billing">
              {t("settings:sections.billing")}
            </TabsTab>
          )}
          <TabsTab value="legal" data-test="settings-tab-legal">
            {t("settings:sections.legal")}
          </TabsTab>
          {showTesting && (
            <TabsTab value="testing" data-test="settings-tab-testing">
              {t("settings:sections.testing")}
            </TabsTab>
          )}
        </TabsList>

        <div className="min-w-0 flex-1">
          <TabsPanel value="company" keepMounted={visitedTabs.has("company")}>
            <SettingsCompanySection
              profileData={profileData}
              profileStatus={profileStatus}
              profileError={profileError}
              profileLoading={profileLoading}
              profileSaving={profileSaving}
              onProfileChange={handleProfileChange}
              onIndustryToggle={handleIndustryToggle}
              onCertificateToggle={handleCertificateToggle}
              onProfileSave={handleProfileSave}
            />
          </TabsPanel>

          <TabsPanel value="preferences" keepMounted={visitedTabs.has("preferences")}>
            <SettingsPreferencesSection
              appLanguage={appLanguage}
              reportLanguage={reportLanguage}
              defaultMarket={defaultMarket}
              prefStatus={prefStatus}
              onChangeAppLanguage={handleChangeAppLanguage}
              onChangeReportLanguage={handleChangeReportLanguage}
              onChangeDefaultMarket={handleChangeDefaultMarket}
            />
          </TabsPanel>

          <TabsPanel value="account" keepMounted={visitedTabs.has("account")}>
            <SettingsAccountSection />
          </TabsPanel>

          {showTeams && (
            <TabsPanel value="teams" keepMounted={visitedTabs.has("teams")}>
              <SettingsTeamsSection />
            </TabsPanel>
          )}

          {showStyles && (
            <TabsPanel value="styles" keepMounted={visitedTabs.has("styles")}>
              <SettingsStylesSection />
            </TabsPanel>
          )}

          {showBilling && (
            <TabsPanel value="billing" keepMounted={visitedTabs.has("billing")}>
              <BillingStatus />
            </TabsPanel>
          )}

          <TabsPanel value="legal" keepMounted={visitedTabs.has("legal")}>
            <SettingsLegalSection />
          </TabsPanel>

          {showTesting && (
            <TabsPanel value="testing" keepMounted={visitedTabs.has("testing")}>
              <SettingsTestingSection />
            </TabsPanel>
          )}
        </div>
      </Tabs>
    </Page>
  );
}